'use client';

import { useRef, useState, useEffect } from 'react';

interface TrendVideoMediaProps {
  src: string;
  poster?: string | null;
  alt: string;
}

/**
 * Lazy auto-playing video for trend cards.
 *
 * Lifecycle (managed by two IntersectionObservers):
 *   1. Far from viewport -> only poster <img> in DOM
 *   2. Within ~200px of viewport -> <video> mounts on top of poster
 *   3. >=35% visible -> video.play()
 *   4. <35% visible -> video.pause()
 *   5. Leaves 200px zone -> <video> removed from DOM
 */
export function TrendVideoMedia({ src, poster, alt }: TrendVideoMediaProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isNearViewport, setIsNearViewport] = useState(false);
  const [shouldPlay, setShouldPlay] = useState(false);
  const [posterLoaded, setPosterLoaded] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) setIsNearViewport(entry.isIntersecting);
      },
      { rootMargin: '200px 0px' },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      setShouldPlay(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) {
          setShouldPlay(entry.isIntersecting && entry.intersectionRatio >= 0.35);
        }
      },
      { threshold: [0, 0.35, 0.6] },
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, [isNearViewport]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (shouldPlay) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [shouldPlay]);

  return (
    <div ref={containerRef} className="relative h-full w-full overflow-hidden">
      {poster && (
        <img
          src={poster}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          onLoad={() => setPosterLoaded(true)}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${
            posterLoaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}
      {isNearViewport && (
        <video
          ref={videoRef}
          src={src}
          poster={poster || undefined}
          muted
          loop
          playsInline
          preload="metadata"
          onLoadedData={() => setVideoLoaded(true)}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${
            videoLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          aria-label={alt}
        />
      )}
    </div>
  );
}
